<template>
  <PageLoading v-if="loading || (superPacmanInstallment && !showNoPayments && !flagsSettled)" />
  <ErrorMessage v-if="error" :country-prop="country" :error-title="errorTitle" :error-description="errorDescription" />
  <SuperPacmanInstallmentList
    v-else-if="superPacmanInstallment && !showNoPayments && flagsSettled && useRedesign"
    :installment-details="superPacmanInstallment"
    @select-installment-item="handleSelectInstallment"
    @other-amount="handleOtherAmount"
  />
  <SuperPacmanInstallmentListLegacy
    v-else-if="superPacmanInstallment && !showNoPayments && flagsSettled && !useRedesign"
    :installment-details="superPacmanInstallment"
    :kasnet-qr-available="kasnetQrAvailable"
    @select-installment-item="handleSelectInstallment"
    @other-amount="handleOtherAmount"
  />
  <NoPayments v-else-if="showNoPayments" :country-prop="country" />
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import NoPayments from "@/components/NoPayments.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import { useSuperPacmanInstallments } from "@/services/DataService";
import {
  ISuperPacmanFetchInstallmentRequest,
  ISuperPacmanInstallment,
  ISuperPacmanInstallmentItem,
} from "@/types/ISuperPacman";
import PageLoading from "@/components/PageLoading.vue";
import SuperPacmanInstallmentList from "@/components/SuperPacmanInstallmentList.vue";
import SuperPacmanInstallmentListLegacy from "@/components/SuperPacmanInstallmentListLegacy.vue";
import { getFallbackLocale } from "@/i18n";
import authHelper from "@/helpers/authHelper";
import { clearResumeProvidersSelection, getResumeProvidersSelection } from "@/helpers/resumeProvidersStorage";
import { Countries } from "@/types/Countries";
import { Providers } from "@/types/Providers";
import { isUIFlagEnabled, PAYMENT_FLOW_REDESIGN_FLAG } from "@/helpers/uiFlags";

export default defineComponent({
  name: "SuperPacmanInstallmentsView",
  components: {
    ErrorMessage,
    NoPayments,
    PageLoading,
    SuperPacmanInstallmentList,
    SuperPacmanInstallmentListLegacy,
  },
  emits: ["select-installment-item", "other-amount"],
  setup() {
    const { n, t, te } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    // Extract device tag from route
    const deviceTag = computed<string>(() => route.params.deviceTag as string);
    // Initialize store with device tag
    if (deviceTag.value) {
      store.dispatch("payOptions/setDeviceTag", deviceTag.value);
    }
    // Determine payment option from query params
    const showOptions = computed<string>(() => {
      const option = route.query.showoptions;
      if (option === "2" || option === "option_b") {
        return "option_b";
      }
      return "option_a";
    });
    store.dispatch("payOptions/setPaymentOption", showOptions.value);

    // Error state management
    const errorTitle = ref<string>(t("unexpected-error"));
    const errorDescription = ref<string>(t("try-again-please"));

    // Fetch installments
    const request: ISuperPacmanFetchInstallmentRequest = { deviceTag: deviceTag.value, optionName: showOptions.value };

    const { superPacmanInstallment, error } = useSuperPacmanInstallments(request);

    // Computed properties with proper types and fallbacks
    const loading = computed<boolean>(() => !superPacmanInstallment.value);

    const country = computed<string | undefined>(() => superPacmanInstallment.value?.financeProduct.country);

    const flagsSettled = ref<boolean>(false);
    const useRedesign = computed<boolean>(() =>
      isUIFlagEnabled(store.state.payOptions.uiFlags, PAYMENT_FLOW_REDESIGN_FLAG),
    );

    const showNoPayments = computed<boolean>(() => {
      if (!superPacmanInstallment.value) return false;

      const noDaysToAmount = superPacmanInstallment.value.daysToAmount.length === 0;
      const fullPayoffEqualsZero = Number(superPacmanInstallment.value.financeProduct.fullPayoffAmount) === 0;

      return noDaysToAmount && fullPayoffEqualsZero;
    });

    return {
      n,
      t,
      te,
      superPacmanInstallment,
      router,
      store,
      loading,
      error,
      country,
      showNoPayments,
      errorTitle,
      errorDescription,
      flagsSettled,
      useRedesign,
    };
  },
  data() {
    return {
      kasnetQrAvailable: false,
    };
  },
  watch: {
    superPacmanInstallment(newValue: ISuperPacmanInstallment) {
      this.store.dispatch("payOptions/setCountry", newValue.financeProduct.country);
      this.store.dispatch("payOptions/setSuperPacmanInstallment", newValue as ISuperPacmanInstallment);
      // Determine and store currency symbol
      const locale = getFallbackLocale(newValue.financeProduct.country);
      const currencySymbol = this.n(0, "currency", locale).replace(/\s?0+(?:[.,]\d+)?/g, "").trim();
      this.store.dispatch("payOptions/setCurrencySymbol", currencySymbol);
      this.store.dispatch("payOptions/setUserLocale", locale);

      // if no installment comes from the endpoint, show no contract found message
      // currently working for brazil only, cuz its the only country that has the "no-contract-found" message defined
      if (!newValue.financeProduct.financeProductType && this.te("no-contract-found")) {
        this.errorTitle = this.t("no-contract-found");
        this.errorDescription = "";
      }

      if (newValue.financeProduct.country === Countries.PERU) {
        authHelper.getProviders().then(({ providers }) => {
          this.kasnetQrAvailable = providers.some((p) => p.name === Providers.KASNET_QR);
        }).catch(() => {
          this.kasnetQrAvailable = false;
        });
      }

      authHelper
        .getUIFlags()
        .then((flags) => {
          this.store.dispatch("payOptions/setUIFlags", flags);
        })
        .catch(() => undefined)
        .finally(() => {
          this.flagsSettled = true;
        });

      // Resuming from /payment-failed after an external redirect (Xendit GCash /
      // Maya): the store is now re-hydrated with the finance product, so restore
      // the previously selected amount and forward to the provider selection
      // screen instead of leaving the user on amount selection (PYMT-2398).
      if (this.$route.query.resume === "providers") {
        const resume = getResumeProvidersSelection();
        clearResumeProvidersSelection();
        if (resume) {
          this.store.dispatch("payOptions/setSelectedInstallment", resume.selectedInstallment);
          this.store.dispatch("payOptions/setSuperPacmanAmount", resume.superPacmanSelectedAmount);
          this.router.push({ name: "ProvidersByDeviceTagAndCountry", params: { usingSP: "true" } });
        }
      }
    },
  },
  mounted: () => window.scrollTo(0, 0),
  methods: {
    handleSelectInstallment(selectedInstallmentItem: ISuperPacmanInstallmentItem) {
      this.$emit("select-installment-item", selectedInstallmentItem);
      this.router.push({ name: "ProvidersByDeviceTagAndCountry", params: { usingSP: "true" } });
    },
    handleOtherAmount() {
      this.router.push({
        name: "OtherAmountView",
        query: { kasnet: this.kasnetQrAvailable ? "1" : undefined },
      });
    },
  },
});
</script>

<style scoped>
.installment-list {
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
